import { isObject } from '@iconik-io/core/src/is_object';

import {
  type FilterValue,
  type FilterValueStoredIn,
  INCLUDE_ANY_OF,
  type MultiSelectFilterGroup,
  type MultiSelectFilterValue,
} from '../../redux/common_filters/types';
import { isStoredInArray } from './guard_filter_stored_in';

function isMultiSelectGroup(value: unknown): value is MultiSelectFilterGroup {
  return (
    isObject(value) &&
    'modifier' in value &&
    Array.isArray(value.items) &&
    value.items.every((item) => typeof item === 'string')
  );
}

function isMultiSelectGroupArray(
  values: unknown[],
): values is MultiSelectFilterValue {
  return values.every(isMultiSelectGroup);
}

// Empty arrays are rejected: they carry no shape to tell them apart from the
// other array-valued filters.
export function isMultiSelectFilterValue(
  value: FilterValue | undefined,
): value is MultiSelectFilterValue {
  return (
    Array.isArray(value) && value.length > 0 && value.every(isMultiSelectGroup)
  );
}

type Options = {
  convertLegacyStrings?: boolean;
};

export function guardMultiSelectFilter(
  filterValue: unknown,
  { convertLegacyStrings = true }: Options = {},
): MultiSelectFilterValue | undefined {
  if (!Array.isArray(filterValue) || filterValue.length === 0) {
    return undefined;
  }

  const firstValue = filterValue[0];

  // Handle flat string arrays
  if (typeof firstValue === 'string') {
    if (
      convertLegacyStrings &&
      filterValue.every((item): item is string => typeof item === 'string')
    ) {
      // Legacy conversion defaults to INCLUDE_ANY_OF (OR) for backward compatibility
      return [{ modifier: INCLUDE_ANY_OF, items: filterValue }];
    }
    return undefined;
  }

  // Current standard configuration
  if (isMultiSelectGroupArray(filterValue)) {
    return filterValue;
  }

  // Legacy backward compatibility configuration for storage filter
  if (isStoredInArray(filterValue)) {
    return convertStoredInToMultiSelect(filterValue);
  }

  return undefined;
}

function convertStoredInToMultiSelect(
  values: FilterValueStoredIn[],
): MultiSelectFilterValue {
  const includeIds: string[] = [];
  const excludeIds: string[] = [];

  for (const value of values) {
    if (value.operator === 'AND') {
      includeIds.push(value.storageId);
    } else if (value.operator === 'NOT') {
      excludeIds.push(value.storageId);
    }
  }

  const groups: MultiSelectFilterGroup[] = [];

  if (includeIds.length > 0) {
    groups.push({ modifier: 'include_any_of', items: includeIds });
  }

  if (excludeIds.length > 0) {
    groups.push({ modifier: 'exclude_any_of', items: excludeIds });
  }

  return groups;
}
